import { useCallback, useEffect, useState } from 'react';
import { useDeskList } from '@lemontaigu/staff/react';
import type { DeskListEntry } from '@lemontaigu/staff';

// « Liste de la réception » (RH group — ADR-0026, DESK-OPERATOR-AND-MAIL-BRIEF D10).
//
// Who the desk's « Qui êtes-vous ? » shows: anyone the operator ticks here,
// employee or Hors Staff, the operator included. Identifying uses the person's
// pointeuse PIN, so a ticked person without one is listed but cannot identify
// until it is set (Équipe › Pointages, or Hors Staff). Archived people are left
// out: the database refuses them whatever this page says.

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

const fullName = (p: DeskListEntry) => `${p.firstName} ${p.lastName}${p.nickname ? ` (« ${p.nickname} »)` : ''}`;

export function DeskList() {
  const service = useDeskList();
  const [people, setPeople] = useState<DeskListEntry[] | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr('');
    try {
      setPeople(await service.load());
    } catch (e) {
      setErr(errMsg(e));
    }
  }, [service]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (p: DeskListEntry) => {
    setBusy(p.id);
    setErr('');
    try {
      await service.setListed(p.id, !p.listed);
      await load();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const active = (people ?? []).filter((p) => !p.archived);
  const listed = active.filter((p) => p.listed);

  return (
    <div className="equipe hs-page">
      <div className="equipe-head">
        <h2 className="section-title">Liste de la réception</h2>
        <span className="equipe-hint">
          Les personnes cochées apparaissent dans « Qui êtes-vous ? » sur pms.*, reception.* et bos.*. Elles
          s’identifient avec leur code de la pointeuse ; leur nom est alors inscrit sur les réservations, les
          paiements, les changements de chambre et les refus qu’elles saisissent.
        </span>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {people === null && !err && <p className="staff-empty">Chargement…</p>}

      {people !== null && (
        <section className="hs-section">
          <h3>
            Sur la liste : {listed.length} / {active.length}
          </h3>
          <table className="ts-table">
            <tbody>
              {active.map((p) => (
                <tr key={p.id}>
                  <td>
                    <label className="desk-list-row">
                      <input
                        type="checkbox"
                        checked={p.listed}
                        disabled={busy !== null}
                        onChange={() => void toggle(p)}
                      />{' '}
                      {fullName(p)}
                    </label>
                  </td>
                  <td>{p.kind === 'trusted' ? 'Hors Staff' : 'Équipe'}</td>
                  <td className={p.hasPin ? 'emp-ok' : 'emp-warn'}>
                    {p.hasPin ? '● Code pointeuse' : p.listed ? '○ Code à définir — ne peut pas s’identifier' : '○ Pas de code'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="equipe-hint">
            Le code se définit dans Équipe › Pointages (salariés) ou Hors Staff. Une personne archivée ne peut pas
            s’identifier.
          </p>
        </section>
      )}
    </div>
  );
}
